/**
 * RaceTrace Live, track map: the room the track is drawn in, clear of the panels floating over the
 * map (the key and the pit list on a wide screen, the box call and closing panels, the flag badge,
 * the turn buttons). Each panel against an edge takes its depth off one edge it is against, so the
 * track is fitted beside them instead of under them. Which edge: with the track's size known, the
 * combination of edges that leaves the track drawn largest (a wide track gives up width, not
 * height); without it, each panel the edge costing the least area. A side never takes more than
 * MAX_SIDE of the map, so a crowded map still shows the track.
 */

export interface Rect {
  x: number;
  y: number;
  w: number;
  h: number;
}

export interface Insets {
  l: number;
  t: number;
  r: number;
  b: number;
}

/** A panel this near an edge (px) is against it. */
const EDGE_PX = 30;
/** No side takes more than this share of the map's width or height. */
const MAX_SIDE = 0.45;
/** A gap kept between a panel and the track (px). */
const GAP_PX = 6;

/** Each panel's ways out: the edges it is against and the depth it takes off each (none: it is in the map's middle). */
function cutsOf(panels: Rect[], W: number, H: number): [keyof Insets, number, number][][] {
  const out: [keyof Insets, number, number][][] = [];
  for (const p of panels) {
    const x0 = Math.max(0, p.x);
    const y0 = Math.max(0, p.y);
    const x1 = Math.min(W, p.x + p.w);
    const y1 = Math.min(H, p.y + p.h);
    if (x1 - x0 < 2 || y1 - y0 < 2) continue;
    const cuts: [keyof Insets, number, number][] = [];
    if (x0 <= EDGE_PX) cuts.push(["l", x1 + GAP_PX, (x1 + GAP_PX) * H]);
    if (x1 >= W - EDGE_PX) cuts.push(["r", W - x0 + GAP_PX, (W - x0 + GAP_PX) * H]);
    if (y0 <= EDGE_PX) cuts.push(["t", y1 + GAP_PX, (y1 + GAP_PX) * W]);
    if (y1 >= H - EDGE_PX) cuts.push(["b", H - y0 + GAP_PX, (H - y0 + GAP_PX) * W]);
    if (cuts.length) out.push(cuts);
  }
  return out;
}

const capped = (r: Insets, W: number, H: number): Insets => ({
  l: Math.min(r.l, W * MAX_SIDE),
  t: Math.min(r.t, H * MAX_SIDE),
  r: Math.min(r.r, W * MAX_SIDE),
  b: Math.min(r.b, H * MAX_SIDE),
});

/**
 * The panels' rectangles (in the map's own px) → how much each edge gives up. With `track` (the
 * turned track's width and height in its own units, and the padding kept round it): every
 * combination of the panels' edges is tried, and the one that draws the track largest kept (the
 * least area given up on a tie).
 */
export function mapInsets(panels: Rect[], W: number, H: number, track?: { bw: number; bh: number; pad: number }): Insets {
  const ways = cutsOf(panels, W, H);
  if (track && ways.length && ways.length <= 10) {
    let best: { r: Insets; scale: number; area: number } | null = null;
    const pick = (i: number, r: Insets, area: number) => {
      if (i === ways.length) {
        const c = capped(r, W, H);
        const scale = Math.min((W - c.l - c.r - 2 * track.pad) / track.bw, (H - c.t - c.b - 2 * track.pad) / track.bh);
        if (!best || scale > best.scale + 1e-9 || (Math.abs(scale - best.scale) <= 1e-9 && area < best.area)) best = { r: c, scale, area };
        return;
      }
      for (const [side, depth, a] of ways[i]) pick(i + 1, { ...r, [side]: Math.max(r[side], depth) }, area + a);
    };
    pick(0, { l: 0, t: 0, r: 0, b: 0 }, 0);
    return best!.r;
  }
  const out: Insets = { l: 0, t: 0, r: 0, b: 0 };
  for (const p of panels) {
    const x0 = Math.max(0, p.x);
    const y0 = Math.max(0, p.y);
    const x1 = Math.min(W, p.x + p.w);
    const y1 = Math.min(H, p.y + p.h);
    if (x1 - x0 < 2 || y1 - y0 < 2) continue;
    // Each edge it is against: the room it would take (area), the least wins.
    const cuts: [keyof Insets, number, number][] = [];
    if (x0 <= EDGE_PX) cuts.push(["l", x1 + GAP_PX, (x1 + GAP_PX) * H]);
    if (x1 >= W - EDGE_PX) cuts.push(["r", W - x0 + GAP_PX, (W - x0 + GAP_PX) * H]);
    if (y0 <= EDGE_PX) cuts.push(["t", y1 + GAP_PX, (y1 + GAP_PX) * W]);
    if (y1 >= H - EDGE_PX) cuts.push(["b", H - y0 + GAP_PX, (H - y0 + GAP_PX) * W]);
    if (!cuts.length) continue;
    const [side, depth] = cuts.reduce((a, c) => (c[2] < a[2] ? c : a));
    out[side] = Math.max(out[side], depth);
  }
  out.l = Math.min(out.l, W * MAX_SIDE);
  out.r = Math.min(out.r, W * MAX_SIDE);
  out.t = Math.min(out.t, H * MAX_SIDE);
  out.b = Math.min(out.b, H * MAX_SIDE);
  return out;
}
